import { useState, useEffect, useRef, useMemo, useCallback } from 'react';

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getPromotionDetails, changeGetProduct } from '../../../../data/promotions';
import {
    addToCart,
    PLATFORMS,
    getCartList,
    resolveCartPlatform,
    getSelectedCustomerFromStorage,
} from '../../../../data/cart';
import { getCatalogProductsByIds } from '../../../../data/epos';
import { prepareProductForCart } from '../../Epos/utils/cartCalculations';
import { mergeCartListFromAddResponse } from '../../Epos/hooks/useCartApi';
import { notifyPromotionMessage } from '../../../../utils/promotionMessageToast';
import { useRole } from '../../../../contexts/RoleContext';
import { getPromotionSwapData, setPromotionSwapData, clearPromotionSwapData } from '../utils/promotionHelpers';
import {
    getPromotionLineStockCap,
    buildPromotionMaxGetBlockedDialogState,
} from '../utils/promotionLineStock';
import {
    buildPromotionCartQuantityMaps,
    calculateMaxAllowedGetQuantity as calculateMaxAllowedGetQuantityFromMaps,
    getPromotionOfferProductUnitPrice,
    findPromotionCartLine,
    resolveMandatoryGetMinQuantity,
    isCompulsoryOfferProduct,
} from '../utils/promotionOfferProductsShared';
import { getMissingCompulsoryProductCodes } from '../utils/compulsoryProducts';
import {
    capQuantityToPurchaseLimit,
    clampToPromotionProductMax,
    mergeOrderLimitFieldsFromCartLines,
    validateMaximumOrderQuantityForLineQty,
    getMaximumOrderQuantityFromItem,
    getCartProductQuantityTotal,
    buildPurchaseLimitExceededMessage,
    enrichPromotionBuyProduct,
    EMPTY_SALES_LIMIT_DIALOG,
    buildStockLimitDialogState,
    buildPurchaseLimitDialogState,
} from '../../common/maximumOrderQuantity';

export const usePromotionDrawer = (open, promotion, onClose, options = {}) => {
    const { activeRole, hasPermission } = useRole();
    const queryClient = useQueryClient();
    const [activeTab, setActiveTab] = useState(0);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedBuyGroupId, setSelectedBuyGroupId] = useState(null);
    const [selectedGetGroupId, setSelectedGetGroupId] = useState(null);
    const [productQuantities, setProductQuantities] = useState({});
    const [productPricingTypes, setProductPricingTypes] = useState({});
    const [existingCartQuantities, setExistingCartQuantities] = useState({});
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success'
    });
    const [limitDialog, setLimitDialog] = useState(EMPTY_SALES_LIMIT_DIALOG);
    const [swapData, setSwapData] = useState({ removed: [], added: [] });
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [isSubmittingSwap, setIsSubmittingSwap] = useState(false);
    const [showDiscardDialog, setShowDiscardDialog] = useState(false);
    const [showInvalidProductsDialog, setShowInvalidProductsDialog] = useState(false);
    const [showSwapErrorDialog, setShowSwapErrorDialog] = useState(false);
    const [swapErrorMessage, setSwapErrorMessage] = useState('');
    const [showCompulsoryDialog, setShowCompulsoryDialog] = useState(false);
    const [compulsoryDialogCodes, setCompulsoryDialogCodes] = useState([]);
    const [hasAcknowledgedCompulsoryDialog, setHasAcknowledgedCompulsoryDialog] = useState(false);
    const lastUIQuantitiesRef = useRef({});

    const openStockLimitDialog = useCallback((product, capFloor) => {
        setLimitDialog(
            buildStockLimitDialogState(
                {
                    product_name: product?.product_name || 'Product',
                    product_code: product?.product_code || product?.code || '',
                },
                capFloor
            )
        );
    }, []);

    const openPurchaseLimitDialog = useCallback((product, message) => {
        setLimitDialog(
            buildPurchaseLimitDialogState(
                {
                    product_name: product?.product_name || 'Product',
                    product_code: product?.product_code || product?.code || '',
                },
                message
            )
        );
    }, []);

    const closeLimitDialog = useCallback(() => {
        setLimitDialog(EMPTY_SALES_LIMIT_DIALOG);
    }, []);

    const openMaxGetBlockedDialog = useCallback((product) => {
        setLimitDialog(buildPromotionMaxGetBlockedDialogState(product));
    }, []);

    const resolveProductGroupId = (product) => {
        const g =
            product?.group_id ??
            product?.group_no ??
            product?.promoGroupId ??
            product?.promo_group_id;
        if (g === null || g === undefined) return null;
        const s = String(g).trim();
        return s === '' ? null : s;
    };

    const getCustomerAndPlatform = () => {
        if (!activeRole) {
            return { customer: null, platform: null, isEpos: null };
        }

        const roleName = activeRole?.name ? activeRole.name.toLowerCase() : '';
        const hasPOSPermission = hasPermission('POS');
        const hasSalesQuickOrderPermission = hasPermission('SalesQuickOrder');

        let isEpos = false;

        if (hasPOSPermission && !hasSalesQuickOrderPermission) {
            isEpos = true;
        } else if (hasSalesQuickOrderPermission && !hasPOSPermission) {
            isEpos = false;
        } else if (hasPOSPermission && hasSalesQuickOrderPermission) {
            if (roleName.includes('till') || roleName.includes('cashier')) {
                isEpos = true;
            } else if (roleName.includes('tele calling') || roleName.includes('telecaller')) {
                isEpos = false;
            } else {
                return { customer: null, platform: null, isEpos: null };
            }
        } else {
            return { customer: null, platform: null, isEpos: null };
        }

        const platform = isEpos ? PLATFORMS.POS : PLATFORMS.TELE_CALLING;
        const customerStorageKey = isEpos ? 'epos_selectedCustomer' : 'quickOrder_selectedCustomer';
        const customerStorage = isEpos ? localStorage : sessionStorage;

        const savedCustomer = customerStorage.getItem(customerStorageKey);
        if (!savedCustomer) {
            return { customer: null, platform, isEpos };
        }

        try {
            const customer = JSON.parse(savedCustomer);
            if (!customer?.id) {
                return { customer: null, platform, isEpos };
            }
            return { customer, platform, isEpos };
        } catch (e) {
            return { customer: null, platform, isEpos };
        }
    };

    const resolved = getCustomerAndPlatform();
    const selectedPlatform =
        resolveCartPlatform({ platform: options?.platform, isEpos: options?.isEpos }) ??
        resolved.platform ??
        PLATFORMS.TELE_CALLING;
    const selectedCustomer =
        options?.selectedCustomer !== undefined
            ? options.selectedCustomer
            : getSelectedCustomerFromStorage(selectedPlatform) ?? resolved.customer;
    const isEpos = selectedPlatform === PLATFORMS.POS;

    const { data: promotionData, isLoading, error, refetch: refetchPromotionDetails } = useQuery({
        queryKey: ['promotionDetails', promotion?.id, selectedCustomer?.id],
        queryFn: () =>
            getPromotionDetails(promotion?.id, {
                customerId: selectedCustomer?.id,
                channels: selectedPlatform,
            }),
        enabled: !!promotion?.id && open,
        staleTime: 0,
        cacheTime: 0,
        refetchOnMount: true,
        refetchOnWindowFocus: false,
    });

    const { data: cartData, refetch: refetchCart } = useQuery({
        queryKey: ['cartList', selectedCustomer?.id, selectedPlatform],
        queryFn: () => getCartList(selectedCustomer.id, selectedPlatform),
        enabled: !!selectedCustomer?.id && !!selectedPlatform && !!promotion?.id && open,
        staleTime: 0,
        gcTime: 1000 * 60,
        refetchOnMount: true,
        refetchOnWindowFocus: false,
    });

    useEffect(() => {
        if (open && promotion?.id) {
            setActiveTab(0);
            setSearchTerm('');
            setSelectedBuyGroupId(null);
            setSelectedGetGroupId(null);
            setProductQuantities({});
            setProductPricingTypes({});
            setExistingCartQuantities({});
            lastUIQuantitiesRef.current = {};
            const savedSwapData = getPromotionSwapData(promotion.id);
            setSwapData(savedSwapData);
            setHasAcknowledgedCompulsoryDialog(false);
            refetchPromotionDetails();
            if (selectedCustomer?.id && selectedPlatform) {
                refetchCart();
            }
        }
    }, [open, promotion?.id, refetchPromotionDetails, refetchCart, selectedCustomer?.id, selectedPlatform]);

    useEffect(() => {
        if (promotion?.id && open && cartData !== undefined && promotionData?.data) {
            if (cartData?.success && cartData?.data && Array.isArray(cartData.data) && cartData.data.length > 0) {
                const cartItems = cartData.data;
                const promotionIdStr = String(promotion.id);
                const promoDetails = promotionData.data;
                const { existingQuantities, pricingTypes } = buildPromotionCartQuantityMaps(
                    cartItems,
                    promoDetails,
                    promotionIdStr
                );
                const quantities = { ...existingQuantities };

                const hasBuyProducts = Object.keys(quantities).some(key =>
                    key.startsWith('buy_') && !key.startsWith('get_') && quantities[key] > 0
                );

                const newQuantities = {};
                Object.keys(quantities).forEach(key => {
                    if (quantities[key] > 0) {
                        if (hasBuyProducts || !key.startsWith('get_')) {
                            newQuantities[key] = quantities[key];
                        }
                    }
                });

                setProductQuantities(newQuantities);
                lastUIQuantitiesRef.current = { ...newQuantities };
                setExistingCartQuantities(existingQuantities);
                setProductPricingTypes(prev => ({ ...prev, ...pricingTypes }));
            } else {
                setProductQuantities({});
                setExistingCartQuantities({});
                setProductPricingTypes({});
                lastUIQuantitiesRef.current = {};
            }
        }
    }, [cartData, promotion?.id, open, promotionData]);

    const cartLineItemsForLimits = useMemo(
        () => (cartData?.success && Array.isArray(cartData.data) ? cartData.data : []),
        [cartData]
    );

    const promotionDetails = promotionData?.data || {};
    const buyProductIdsForCatalog = useMemo(
        () =>
            (promotionDetails.buy_products || [])
                .map((p) => p?.product_id)
                .filter((id) => id !== undefined && id !== null && String(id).trim() !== ''),
        [promotionDetails.buy_products]
    );

    const { data: catalogProductsForLimits = [] } = useQuery({
        queryKey: ['promotionDrawerCatalogLimits', buyProductIdsForCatalog.join(',')],
        queryFn: () => getCatalogProductsByIds(buyProductIdsForCatalog),
        enabled: open && buyProductIdsForCatalog.length > 0,
        staleTime: 1000 * 60 * 5,
        refetchOnWindowFocus: false,
    });

    const catalogByProductId = useMemo(() => {
        const map = new Map();
        (catalogProductsForLimits || []).forEach((row) => {
            const pid = String(row?.product_id ?? row?.id ?? '').trim();
            if (pid) map.set(pid, row);
        });
        return map;
    }, [catalogProductsForLimits]);

    const buyProductsWithLimits = useMemo(
        () =>
            (promotionDetails.buy_products || []).map((product) =>
                enrichPromotionBuyProduct(
                    product,
                    catalogByProductId.get(String(product?.product_id ?? '').trim()),
                    cartLineItemsForLimits
                )
            ),
        [promotionDetails.buy_products, catalogByProductId, cartLineItemsForLimits]
    );

    const buyProductGroupIds = useMemo(() => {
        const products = promotionDetails.buy_products || [];
        const seen = new Set();
        const ordered = [];
        products.forEach((p) => {
            const gid = resolveProductGroupId(p);
            if (gid !== null && !seen.has(gid)) {
                seen.add(gid);
                ordered.push(gid);
            }
        });
        ordered.sort((a, b) => {
            const na = Number(a);
            const nb = Number(b);
            if (!Number.isNaN(na) && !Number.isNaN(nb) && String(na) === String(a) && String(nb) === String(b)) {
                return na - nb;
            }
            return String(a).localeCompare(String(b), undefined, { numeric: true });
        });
        return ordered;
    }, [promotionDetails.buy_products]);

    const getProductGroupIds = useMemo(() => {
        const products = promotionDetails.get_products || [];
        const seen = new Set();
        const ordered = [];
        products.forEach((p) => {
            const gid = resolveProductGroupId(p);
            if (gid !== null && !seen.has(gid)) {
                seen.add(gid);
                ordered.push(gid);
            }
        });
        ordered.sort((a, b) => {
            const na = Number(a);
            const nb = Number(b);
            if (!Number.isNaN(na) && !Number.isNaN(nb) && String(na) === String(a) && String(nb) === String(b)) {
                return na - nb;
            }
            return String(a).localeCompare(String(b), undefined, { numeric: true });
        });
        return ordered;
    }, [promotionDetails.get_products]);

    useEffect(() => {
        if (!open) return;
        if (buyProductGroupIds.length === 0) {
            setSelectedBuyGroupId(null);
            return;
        }
        setSelectedBuyGroupId((prev) =>
            prev != null && buyProductGroupIds.some((id) => String(id) === String(prev))
                ? prev
                : buyProductGroupIds[0]
        );
    }, [open, promotion?.id, buyProductGroupIds]);

    useEffect(() => {
        if (!open) return;
        if (getProductGroupIds.length === 0) {
            setSelectedGetGroupId(null);
            return;
        }
        setSelectedGetGroupId((prev) =>
            prev != null && getProductGroupIds.some((id) => String(id) === String(prev))
                ? prev
                : getProductGroupIds[0]
        );
    }, [open, promotion?.id, getProductGroupIds]);

    const hasGetProducts = (promotionDetails.get_products || []).length > 0;
    const getQuantity = parseInt(promotionDetails?.get_quantity || 0, 10);

    const calculateMaxAllowedGetQuantity = () =>
        calculateMaxAllowedGetQuantityFromMaps(
            promotionDetails,
            existingCartQuantities,
            productQuantities
        );

    const selectedBuyProductsCount = new Set(
        Object.keys(productQuantities)
            .filter(key => key.startsWith('buy_') && productQuantities[key] > 0)
            .map(key => key.split('_')[1])
    ).size;
    
    const selectedGetProductsCount = new Set(
        Object.keys(productQuantities)
            .filter(key => key.startsWith('get_') && productQuantities[key] > 0)
            .map(key => key.split('_')[1])
    ).size;

    const currentPromoId = promotion?.id ? String(promotion.id) : null;
    const hasCurrentPromotionInCart = cartData?.success && cartData?.data && currentPromoId
        ? cartData.data.some(item => {
            const itemPromoId = item.promo_id ? String(item.promo_id) : null;
            return itemPromoId === currentPromoId;
        })
        : false;

    const hasOtherPromotionsInCart = cartData?.success && cartData?.data && currentPromoId
        ? cartData.data.some(item => {
            const itemPromoId = item.promo_id ? String(item.promo_id) : null;
            return itemPromoId !== null && itemPromoId !== currentPromoId;
        })
        : false;

    const shouldLockOfferProducts = hasOtherPromotionsInCart && !hasCurrentPromotionInCart;

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
        if (selectedCustomer?.id && selectedPlatform && promotion?.id) {
            refetchCart();
        }
    };

    useEffect(() => {
        if (!hasGetProducts && activeTab === 1) {
            setActiveTab(0);
        }
    }, [hasGetProducts, activeTab]);

    const getProductPrice = getPromotionOfferProductUnitPrice;

    const resolveBuyProductForLimit = (product, productId, orderType) => {
        const cartItemsList =
            cartData?.success && Array.isArray(cartData.data) ? cartData.data : [];
        const existingLine = findPromotionCartLine(cartItemsList, {
            productId,
            orderType,
            tabType: 'buy',
        });
        return mergeOrderLimitFieldsFromCartLines(
            existingLine ? { ...product, ...existingLine } : product,
            cartItemsList
        );
    };

    const showPromotionProductLimitDialog = useCallback(
        (product, attemptedQty, pricingType) => {
            if (!product) return;
            const cartItemsList =
                cartData?.success && Array.isArray(cartData.data) ? cartData.data : [];
            const unifiedCap = clampToPromotionProductMax(
                product,
                cartItemsList,
                pricingType,
                attemptedQty,
                getPromotionLineStockCap
            );
            if (!unifiedCap.wasCapped) return;
            if (unifiedCap.limitType === 'stock' && unifiedCap.stockMax != null) {
                openStockLimitDialog(product, unifiedCap.stockMax);
            } else {
                openPurchaseLimitDialog(
                    product,
                    unifiedCap.purchaseMessage ||
                        buildPurchaseLimitExceededMessage(
                            product,
                            unifiedCap.purchaseMax ?? unifiedCap.maxAllowed
                        )
                );
            }
        },
        [cartData, openStockLimitDialog, openPurchaseLimitDialog]
    );

    const notifyQuantityLimitExceeded = useCallback(
        (productId, attemptedQty, tabType, pricingType) => {
            const finalPricingType = pricingType || 'Box';
            if (tabType === 'buy') {
                const product = buyProductsWithLimits.find(
                    (p) => String(p.product_id) === String(productId)
                );
                showPromotionProductLimitDialog(product, attemptedQty, finalPricingType);
                return;
            }
            if (tabType !== 'get') return;
            const product = (promotionDetails.get_products || []).find(
                (p) => String(p.product_id) === String(productId)
            );
            const lineStockCap = product
                ? getPromotionLineStockCap(product, finalPricingType)
                : null;
            if (lineStockCap === null) return;
            const capFloor = Math.max(0, Math.floor(lineStockCap));
            if (attemptedQty > capFloor) {
                openStockLimitDialog(product, capFloor);
            }
        },
        [
            buyProductsWithLimits,
            promotionDetails.get_products,
            showPromotionProductLimitDialog,
            openStockLimitDialog,
        ]
    );

    const handleQuantityChange = (productId, value, tabType, pricingType, options = {}) => {
        let quantity = typeof value === 'number' ? value : parseInt(value) || 0;
        
        const product = tabType === 'buy' 
            ? buyProductsWithLimits.find(p => String(p.product_id) === String(productId))
            : (promotionDetails.get_products || []).find(p => String(p.product_id) === String(productId));
        
        const finalPricingType = pricingType || 'Box';
        const uniqueKey = `${tabType}_${productId}_${finalPricingType}`;
        
        const isCompulsory = isCompulsoryOfferProduct(product);
        const mandatoryMinQty =
            tabType === 'get' && isCompulsory && product
                ? resolveMandatoryGetMinQuantity(
                      product,
                      promotionDetails,
                      existingCartQuantities,
                      productQuantities,
                      {
                          cartItems: cartLineItemsForLimits,
                          promotionIdStr: currentPromoId || '',
                      }
                  )
                : 0;
        const existingQty = existingCartQuantities[uniqueKey] || 0;

        if (tabType === 'buy' && quantity > 0 && product) {
            const cartItemsList =
                cartData?.success && Array.isArray(cartData.data) ? cartData.data : [];
            const unifiedCap = clampToPromotionProductMax(
                product,
                cartItemsList,
                finalPricingType,
                quantity,
                getPromotionLineStockCap
            );
            if (unifiedCap.wasCapped) {
                if (!options.suppressLimitDialog) {
                    showPromotionProductLimitDialog(product, quantity, finalPricingType);
                }
                quantity = unifiedCap.quantity;
            }
        }

        if (tabType !== 'get') {
            const lineStockCap = product ? getPromotionLineStockCap(product, finalPricingType) : null;
            if (lineStockCap !== null) {
                const capFloor = Math.max(0, Math.floor(lineStockCap));
                if (quantity > capFloor) {
                    if (!options.suppressLimitDialog) {
                        openStockLimitDialog(product, capFloor);
                    }
                    const cappedQty = capFloor;
                    const prevUIQty =
                        lastUIQuantitiesRef.current[uniqueKey] !== undefined &&
                        lastUIQuantitiesRef.current[uniqueKey] !== null
                            ? lastUIQuantitiesRef.current[uniqueKey]
                            : productQuantities[uniqueKey] !== undefined &&
                                productQuantities[uniqueKey] !== null
                              ? productQuantities[uniqueKey]
                              : existingQty;
                    lastUIQuantitiesRef.current[uniqueKey] = cappedQty;
                    setProductQuantities((prev) => ({
                        ...prev,
                        [uniqueKey]: cappedQty,
                    }));
                    if (tabType === 'buy' && product && cappedQty !== prevUIQty) {
                        handleAddToCart(
                            product,
                            cappedQty,
                            finalPricingType,
                            tabType,
                            cappedQty - prevUIQty
                        );
                    }
                    return false;
                }
            }
        }

        if (tabType === 'get' && isCompulsory) {
            const uiQty = Number(productQuantities[uniqueKey] || 0);
            if (quantity === 0 && (existingQty > 0 || uiQty > 0)) {
                setSnackbar({
                    open: true,
                    message: 'This is a compulsory product and cannot be removed',
                    severity: 'warning'
                });
                return false;
            }
            if (mandatoryMinQty > 0 && quantity > 0 && quantity < mandatoryMinQty) {
                setSnackbar({
                    open: true,
                    message: `Minimum quantity for this compulsory product is ${mandatoryMinQty}`,
                    severity: 'warning'
                });
                return false;
            }
        }

        if (tabType === 'get' && promotion?.id) {

            if (getQuantity > 0) {
                const maxAllowedGetQty = calculateMaxAllowedGetQuantity();
                const updatedQuantities = { ...productQuantities };
                updatedQuantities[uniqueKey] = quantity;
                
                let finalTotalQty = 0;
                Object.keys(existingCartQuantities)
                    .filter(key => key.startsWith('get_') && !key.startsWith('buy_'))
                    .forEach(key => {
                        const existingQty = existingCartQuantities[key] || 0;
                        const uiQty = updatedQuantities[key];
                        if (uiQty !== undefined && uiQty !== null) {
                            finalTotalQty += uiQty;
                        } else {
                            finalTotalQty += existingQty;
                        }
                    });

                Object.keys(updatedQuantities)
                    .filter(key => key.startsWith('get_') && !key.startsWith('buy_') && !existingCartQuantities[key])
                    .forEach(key => {
                        finalTotalQty += updatedQuantities[key] || 0;
                    });

                let currentTotal = Object.keys(existingCartQuantities)
                    .filter(key => key.startsWith('get_') && !key.startsWith('buy_'))
                    .reduce((sum, key) => {
                        const uiQty = productQuantities[key];
                        return sum + (uiQty !== undefined && uiQty !== null ? uiQty : existingCartQuantities[key] || 0);
                    }, 0);
                
                currentTotal += Object.keys(productQuantities)
                    .filter(key => key.startsWith('get_') && !key.startsWith('buy_') && !existingCartQuantities[key])
                    .reduce((sum, key) => sum + (productQuantities[key] || 0), 0);

                const isIncreasing = finalTotalQty > currentTotal;
                
                if (isIncreasing && finalTotalQty > maxAllowedGetQty) {
                    openMaxGetBlockedDialog(product);
                    return false;
                }
            }
        }

        const prevUIQty = lastUIQuantitiesRef.current[uniqueKey] !== undefined && lastUIQuantitiesRef.current[uniqueKey] !== null
            ? lastUIQuantitiesRef.current[uniqueKey]
            : (productQuantities[uniqueKey] !== undefined && productQuantities[uniqueKey] !== null
                ? productQuantities[uniqueKey]
                : existingQty);
        const deltaQty = quantity - prevUIQty;

        lastUIQuantitiesRef.current[uniqueKey] = quantity;
        setProductQuantities(prev => {
            const newQuantities = { ...prev };
            newQuantities[uniqueKey] = quantity;
            return newQuantities;
        });

        if (tabType === 'buy' && deltaQty !== 0) {
            const product = buyProductsWithLimits.find(p => String(p.product_id) === String(productId));
            if (product) {
                handleAddToCart(product, quantity, finalPricingType, tabType, deltaQty);
            }
        }

        if (tabType === 'get' && promotion?.id) {
            const existingQty = existingCartQuantities[uniqueKey] || 0;
            const product = (promotionDetails.get_products || []).find(p => String(p.product_id) === String(productId));
            const isCompulsory = isCompulsoryOfferProduct(product);
            
            if (quantity === 0) {
                if (existingQty > 0 && !isCompulsory) {
                    if (product) {
                        setSwapData(prev => {
                            const newData = { ...prev };
                            const removedItem = {
                                product_id: product.product_id,
                                product_name: product.product_name,
                                product_code: product.product_code,
                                product_type: finalPricingType,
                                qty: quantity,
                                new_product_unit_price: getProductPrice(product, finalPricingType)
                            };
                            
                            const existingRemovedIndex = newData.removed.findIndex(
                                item => item.product_id === product.product_id && item.product_type === finalPricingType
                            );
                            
                            if (existingRemovedIndex === -1) {
                                newData.removed.push(removedItem);
                            } else {
                                newData.removed[existingRemovedIndex].qty = quantity;
                                newData.removed[existingRemovedIndex].new_product_unit_price = getProductPrice(product, finalPricingType);
                            }
                            
                            newData.added = newData.added.filter(
                                item => !(item.product_id === product.product_id && item.product_type === finalPricingType)
                            );
                            
                            setPromotionSwapData(promotion.id, newData);
                            return newData;
                        });
                    }
                } else if (!isCompulsory) {
                    setSwapData(prev => {
                        const newData = { ...prev };
                        newData.added = newData.added.filter(
                            item => !(item.product_id === productId && item.product_type === finalPricingType)
                        );
                        setPromotionSwapData(promotion.id, newData);
                        return newData;
                    });
                }
            } else if (quantity > 0 && existingQty > 0 && quantity !== existingQty) {
                const product = (promotionDetails.get_products || []).find(p => String(p.product_id) === String(productId));
                if (product) {
                    setSwapData(prev => {
                        const newData = { ...prev };
                        const addedItem = {
                            product_id: product.product_id,
                            product_name: product.product_name,
                            product_code: product.product_code,
                            product_type: finalPricingType,
                            qty: quantity,
                            new_product_unit_price: getProductPrice(product, finalPricingType)
                        };
                        
                        const existingAddedIndex = newData.added.findIndex(
                            item => item.product_id === product.product_id && item.product_type === finalPricingType
                        );
                        
                        if (existingAddedIndex === -1) {
                            newData.added.push(addedItem);
                        } else {
                            newData.added[existingAddedIndex].qty = quantity;
                            newData.added[existingAddedIndex].new_product_unit_price = getProductPrice(product, finalPricingType);
                        }
                        
                        newData.removed = newData.removed.filter(
                            item => !(item.product_id === product.product_id && item.product_type === finalPricingType)
                        );
                        
                        setPromotionSwapData(promotion.id, newData);
                        return newData;
                    });
                }
            } else if (quantity > 0 && existingQty === 0) {
                const product = (promotionDetails.get_products || []).find(p => String(p.product_id) === String(productId));
                if (product) {
                    setSwapData(prev => {
                        const newData = { ...prev };
                        const addedItem = {
                            product_id: product.product_id,
                            product_name: product.product_name,
                            product_code: product.product_code,
                            product_type: finalPricingType,
                            qty: quantity,
                            new_product_unit_price: getProductPrice(product, finalPricingType)
                        };
                        
                        const existingAddedIndex = newData.added.findIndex(
                            item => item.product_id === product.product_id && item.product_type === finalPricingType
                        );
                        
                        if (existingAddedIndex === -1) {
                            newData.added.push(addedItem);
                        } else {
                            newData.added[existingAddedIndex].qty = quantity;
                            newData.added[existingAddedIndex].new_product_unit_price = getProductPrice(product, finalPricingType);
                        }
                        
                        newData.removed = newData.removed.filter(
                            item => !(item.product_id === product.product_id && item.product_type === finalPricingType)
                        );
                        
                        setPromotionSwapData(promotion.id, newData);
                        return newData;
                    });
                }
            }
        }

        return true;
    };

    const handlePricingTypeChange = (uniqueKey, pricingType) => {
        setProductPricingTypes(prev => {
            const newTypes = { ...prev };
            newTypes[uniqueKey] = pricingType;
            return newTypes;
        });
    };

    const handleAddToCart = async (product, quantity, pricingType, tabType, deltaQtyOverride) => {
        if (tabType === 'get' && (!quantity || quantity === 0)) {
            setSnackbar({
                open: true,
                message: 'Please enter a quantity',
                severity: 'warning'
            });
            return;
        }

        const isCompulsory = isCompulsoryOfferProduct(product);
        const mandatoryMinQty =
            tabType === 'get' && isCompulsory
                ? resolveMandatoryGetMinQuantity(
                      product,
                      promotionDetails,
                      existingCartQuantities,
                      productQuantities,
                      {
                          cartItems: cartLineItemsForLimits,
                          promotionIdStr: currentPromoId || '',
                      }
                  )
                : 0;
        
        if (tabType === 'get' && mandatoryMinQty > 0 && quantity < mandatoryMinQty) {
            setSnackbar({
                open: true,
                message: `Minimum quantity for this compulsory product is ${mandatoryMinQty}`,
                severity: 'warning'
            });
            return;
        }

        if (tabType === 'get' && shouldLockOfferProducts) {
            setShowInvalidProductsDialog(true);
            return;
        }

        const orderPricing = pricingType === 'Piece' || pricingType === 'piece' ? 'Piece' : 'Box';
        if (tabType !== 'get') {
            const addLineStockCap = product ? getPromotionLineStockCap(product, orderPricing) : null;
            if (addLineStockCap !== null) {
                const capFloor = Math.max(0, Math.floor(addLineStockCap));
                const targetQtyForStock = typeof quantity === 'number' ? quantity : parseInt(quantity, 10) || 0;
                if (targetQtyForStock > capFloor) {
                    openStockLimitDialog(product, capFloor);
                    return;
                }
            }
        }

        if (tabType === 'get') {
            if (!selectedCustomer || !selectedPlatform) {
                const platformName = isEpos ? 'Epos' : 'Tele Caller';
                setSnackbar({
                    open: true,
                    message: `Please select a customer in ${platformName} first`,
                    severity: 'error'
                });
                return;
            }

            if (getQuantity > 0) {
                const maxAllowedGetQty = calculateMaxAllowedGetQuantity();
                const uniqueKey = `${tabType}_${product.product_id}`;
                const updatedQuantities = { ...productQuantities };
                const targetQty = typeof quantity === 'number' ? quantity : parseInt(quantity, 10) || 0;
                updatedQuantities[uniqueKey] = targetQty;

                let finalTotalQty = 0;
                Object.keys(existingCartQuantities)
                    .filter(key => key.startsWith('get_') && !key.startsWith('buy_'))
                    .forEach(key => {
                        const existingQty = existingCartQuantities[key] || 0;
                        const uiQty = updatedQuantities[key];
                        if (uiQty !== undefined && uiQty !== null) {
                            finalTotalQty += uiQty;
                        } else {
                            finalTotalQty += existingQty;
                        }
                    });

                Object.keys(updatedQuantities)
                    .filter(key => key.startsWith('get_') && !key.startsWith('buy_') && !existingCartQuantities[key])
                    .forEach(key => {
                        finalTotalQty += updatedQuantities[key] || 0;
                    });

                let currentTotal = Object.keys(existingCartQuantities)
                    .filter(key => key.startsWith('get_') && !key.startsWith('buy_'))
                    .reduce((sum, key) => {
                        const uiQty = productQuantities[key];
                        return sum + (uiQty !== undefined && uiQty !== null ? uiQty : existingCartQuantities[key] || 0);
                    }, 0);
                
                currentTotal += Object.keys(productQuantities)
                    .filter(key => key.startsWith('get_') && !key.startsWith('buy_') && !existingCartQuantities[key])
                    .reduce((sum, key) => sum + (productQuantities[key] || 0), 0);

                const isIncreasing = finalTotalQty > currentTotal;

                if (isIncreasing && finalTotalQty > maxAllowedGetQty) {
                    openMaxGetBlockedDialog(product);
                    return;
                }
            }

            const getUniqueKey = `${tabType}_${product.product_id}_${pricingType}`;
            const existingQty = existingCartQuantities[getUniqueKey] || 0;
            const targetQty = typeof quantity === 'number' ? quantity : parseInt(quantity, 10) || 0;
            const deltaQty = targetQty - existingQty;

            if (deltaQty === 0 && existingQty === 0) {
                return;
            }

            const currentSwapData = getPromotionSwapData(promotion.id);
            const newData = { ...currentSwapData };
            
            if (deltaQty > 0) {
                if (existingQty === 0) {
                    const addedItem = {
                        product_id: product.product_id,
                        product_name: product.product_name,
                        product_code: product.product_code,
                        product_type: pricingType,
                        qty: targetQty,
                        new_product_unit_price: getProductPrice(product, pricingType)
                    };
                    
                    const existingAddedIndex = newData.added.findIndex(
                        item => item.product_id === product.product_id && item.product_type === pricingType
                    );
                    
                    if (existingAddedIndex === -1) {
                        newData.added.push(addedItem);
                    } else {
                        newData.added[existingAddedIndex].qty = targetQty;
                        newData.added[existingAddedIndex].new_product_unit_price = getProductPrice(product, pricingType);
                    }
                } else {
                    const existingAddedIndex = newData.added.findIndex(
                        item => item.product_id === product.product_id && item.product_type === pricingType
                    );
                    
                    if (existingAddedIndex === -1) {
                        const addedItem = {
                            product_id: product.product_id,
                            product_name: product.product_name,
                            product_code: product.product_code,
                            product_type: pricingType,
                            qty: targetQty,
                            new_product_unit_price: getProductPrice(product, pricingType)
                        };
                        newData.added.push(addedItem);
                    } else {
                        newData.added[existingAddedIndex].qty = targetQty;
                        newData.added[existingAddedIndex].new_product_unit_price = getProductPrice(product, pricingType);
                    }
                }
                
                const existingRemovedIndex = newData.removed.findIndex(
                    item => item.product_id === product.product_id && item.product_type === pricingType
                );
                
                if (existingRemovedIndex !== -1) {
                    newData.removed.splice(existingRemovedIndex, 1);
                }
            } else if (deltaQty < 0) {
                const productData = (promotionDetails.get_products || []).find(p => String(p.product_id) === String(product.product_id));
                const isCompulsory = isCompulsoryOfferProduct(productData);
                
                if (!isCompulsory) {
                    if (targetQty === 0) {
                        const removedItem = {
                            product_id: product.product_id,
                            product_name: product.product_name,
                            product_code: product.product_code,
                            product_type: pricingType,
                            qty: targetQty,
                            new_product_unit_price: getProductPrice(product, pricingType)
                        };
                        
                        const existingRemovedIndex = newData.removed.findIndex(
                            item => item.product_id === product.product_id && item.product_type === pricingType
                        );
                        
                        if (existingRemovedIndex === -1) {
                            newData.removed.push(removedItem);
                        } else {
                            newData.removed[existingRemovedIndex].qty = targetQty;
                            newData.removed[existingRemovedIndex].new_product_unit_price = getProductPrice(product, pricingType);
                        }
                        
                        const existingAddedIndex = newData.added.findIndex(
                            item => item.product_id === product.product_id && item.product_type === pricingType
                        );
                        
                        if (existingAddedIndex !== -1) {
                            newData.added.splice(existingAddedIndex, 1);
                        }
                    } else {
                        const addedItem = {
                            product_id: product.product_id,
                            product_name: product.product_name,
                            product_code: product.product_code,
                            product_type: pricingType,
                            qty: targetQty,
                            new_product_unit_price: getProductPrice(product, pricingType)
                        };
                        
                        const existingAddedIndex = newData.added.findIndex(
                            item => item.product_id === product.product_id && item.product_type === pricingType
                        );
                        
                        if (existingAddedIndex === -1) {
                            newData.added.push(addedItem);
                        } else {
                            newData.added[existingAddedIndex].qty = targetQty;
                            newData.added[existingAddedIndex].new_product_unit_price = getProductPrice(product, pricingType);
                        }
                        
                        const existingRemovedIndex = newData.removed.findIndex(
                            item => item.product_id === product.product_id && item.product_type === pricingType
                        );
                        
                        if (existingRemovedIndex !== -1) {
                            newData.removed.splice(existingRemovedIndex, 1);
                        }
                    }
                }
            } else if (deltaQty === 0 && existingQty > 0) {
                const addedItem = {
                    product_id: product.product_id,
                    product_name: product.product_name,
                    product_code: product.product_code,
                    product_type: pricingType,
                    qty: targetQty,
                    new_product_unit_price: getProductPrice(product, pricingType)
                };
                
                const existingAddedIndex = newData.added.findIndex(
                    item => item.product_id === product.product_id && item.product_type === pricingType
                );
                
                if (existingAddedIndex === -1) {
                    newData.added.push(addedItem);
                } else {
                    newData.added[existingAddedIndex].qty = targetQty;
                    newData.added[existingAddedIndex].new_product_unit_price = getProductPrice(product, pricingType);
                }
                
                const existingRemovedIndex = newData.removed.findIndex(
                    item => item.product_id === product.product_id && item.product_type === pricingType
                );
                
                if (existingRemovedIndex !== -1) {
                    newData.removed.splice(existingRemovedIndex, 1);
                }
            }
            
            setPromotionSwapData(promotion.id, newData);
            setSwapData(newData);
            
            if (newData.removed.length > 0 || newData.added.length > 0) {
                setShowConfirmDialog(true);
                return;
            }
        }

        const finalUniqueKey = `${tabType}_${product.product_id}_${pricingType}`;
        const existingQty = existingCartQuantities[finalUniqueKey] || 0;
        let targetQty = typeof quantity === 'number' ? quantity : parseInt(quantity, 10) || 0;

        if (tabType === 'buy') {
            const cartItemsList =
                cartData?.success && Array.isArray(cartData.data) ? cartData.data : [];
            const orderType = pricingType === 'Piece' ? 'Piece' : 'Box';
            const unifiedCap = clampToPromotionProductMax(
                product,
                cartItemsList,
                pricingType,
                targetQty,
                getPromotionLineStockCap
            );
            if (unifiedCap.wasCapped) {
                targetQty = unifiedCap.quantity;
            }
            const mergedForLimit = resolveBuyProductForLimit(
                product,
                product.product_id,
                orderType
            );
            const lineLimit = validateMaximumOrderQuantityForLineQty(
                {
                    ...mergedForLimit,
                    product_id: product.product_id,
                    order_type: orderType,
                    piece_type: orderType,
                },
                cartItemsList,
                targetQty
            );
            if (!lineLimit.allowed) {
                const maxQty = getMaximumOrderQuantityFromItem(mergedForLimit);
                if (maxQty != null && maxQty > 0) {
                    const otherQty =
                        getCartProductQuantityTotal(cartItemsList, product.product_id) - existingQty;
                    targetQty = Math.max(
                        0,
                        Math.min(targetQty, Math.floor(maxQty - otherQty))
                    );
                }
            }
        }

        const deltaQty =
            typeof deltaQtyOverride === 'number' && Number.isFinite(deltaQtyOverride)
                ? deltaQtyOverride
                : targetQty - existingQty;

        try {
            if (!selectedCustomer || !selectedPlatform) {
                const platformName = isEpos ? 'Epos' : 'Tele Caller';
                setSnackbar({
                    open: true,
                    message: `Please select a customer in ${platformName} first`,
                    severity: 'error'
                });
                return;
            }

            const platformName = isEpos ? 'Epos' : 'Tele Caller';
            const eventName = isEpos ? 'eposCartUpdated' : 'quickOrderCartUpdated';

            if (deltaQty === 0) {
                return;
            }

            const orderType = pricingType === 'Piece' ? 'Piece' : 'Box';
            const price = pricingType === 'Piece' 
                ? parseFloat(product?.piece_sales_price || product?.piece_offer_price || product?.sales_price || 0)
                : parseFloat(product?.box_sales_price || product?.box_offer_price || product?.sales_price || 0);
            
            const offerPrice = pricingType === 'Piece'
                ? parseFloat(product?.piece_offer_price || 0)
                : parseFloat(product?.box_offer_price || 0);
            
            const finalPrice = offerPrice > 0 ? offerPrice : price;
            const subTotal = finalPrice * Math.abs(deltaQty);

            const productData = {
                id: product.product_id,
                product_code: product.product_code || '',
                product_name: product.product_name || '',
                final_price: pricingType === 'Box' ? finalPrice : 0,
                final_split_price: pricingType === 'Piece' ? finalPrice : 0,
                rate: product.vat_rate || product.tax_rate || 0,
                noOfPieceInBox: product.split || 1,
                is_promoted: tabType === 'get' ? "1" : "0",
                order_type: orderType,
                split: pricingType === "Piece" ? "1" : "0"
            };

            const preparedProduct = prepareProductForCart(productData);

            const cartPayload = {
                id: "",
                customer_id: selectedCustomer.id,
                product_id: product.product_id,
                qty: deltaQty,
                price: finalPrice.toFixed(2),
                piece_type: orderType,
                sub_total: subTotal.toFixed(2),
                is_contra: "0",
                promotion_id: String(promotion?.id || ""),
                is_promoted: tabType === 'get' ? "1" : "0",
                discount_type: "percentage",
                discount_value: "0.00"
            };

            if (tabType === 'get') {
                cartPayload.selected_free_product_id = String(product.product_id);
                cartPayload.selected_PromoID = String(promotion?.id || "");
                cartPayload.selected_free_product_piece_type = pricingType;
            }

            let response;
            try {
                response = await addToCart(cartPayload, selectedPlatform);
            } catch (cartError) {
                const isRemoval = deltaQty < 0;
                const apiMsg =
                    cartError && typeof cartError.message === 'string' ? cartError.message.trim() : '';
                const fallback = isRemoval
                    ? 'Failed to remove product from cart. Please try again.'
                    : 'Failed to add product to cart. Please try again.';
                setSnackbar({
                    open: true,
                    message: apiMsg || fallback,
                    severity: 'error',
                    useErrorDialog: true
                });
                return;
            }

            if (response.success) {
                const customerName = selectedCustomer.customer_name || selectedCustomer.name || 'Customer';
                const isRemoval = deltaQty < 0;
                setSnackbar({
                    open: true,
                    message: isRemoval 
                        ? `Product removed from cart for ${customerName}`
                        : `Product added for ${customerName}`,
                    severity: isRemoval ? 'info' : 'success'
                });
                notifyPromotionMessage(response);

                if (!isEpos) {
                    window.dispatchEvent(new CustomEvent(eventName, {
                        detail: { customerId: selectedCustomer.id }
                    }));
                }

                queryClient.setQueryData(
                    ['cartList', selectedCustomer.id, selectedPlatform],
                    (old) => mergeCartListFromAddResponse(old, response),
                );
                try {
                    const cartRes = await getCartList(
                        selectedCustomer.id,
                        selectedPlatform
                    );
                    queryClient.setQueryData(
                        ['cartList', selectedCustomer.id, selectedPlatform],
                        cartRes
                    );
                    notifyPromotionMessage(cartRes);
                } catch {
                    queryClient.invalidateQueries({
                        queryKey: ['cartList', selectedCustomer.id, selectedPlatform],
                        exact: true,
                    });
                }
            } else {
                const isRemoval = deltaQty < 0;
                setSnackbar({
                    open: true,
                    message: response.message || (isRemoval
                        ? 'Failed to remove product from cart'
                        : 'Failed to add product to cart'),
                    severity: 'error',
                    useErrorDialog: true
                });
            }
        } catch (error) {
            const isRemoval = deltaQty < 0;
            const apiMsg =
                error && typeof error.message === 'string' ? error.message.trim() : '';
            const fallback = isRemoval
                ? 'Failed to remove product from cart. Please try again.'
                : 'Failed to add product to cart. Please try again.';
            setSnackbar({
                open: true,
                message: apiMsg || fallback,
                severity: 'error',
                useErrorDialog: true
            });
        }
    };

    const handleSubmitSwap = async () => {
        if (!promotion?.id || !selectedCustomer?.id || !selectedPlatform) {
            setSnackbar({
                open: true,
                message: 'Please select a customer first',
                severity: 'error'
            });
            return;
        }

        const allNewProducts = [...swapData.added];
        
        Object.keys(existingCartQuantities)
            .filter(key => key.startsWith('get_') && !key.startsWith('buy_'))
            .forEach(key => {
                const keyParts = key.split('_');
                const productId = keyParts[1];
                const orderType = keyParts[2] || 'Box';
                const existingQty = existingCartQuantities[key] || 0;
                
                if (existingQty > 0) {
                    const product = (promotionDetails.get_products || []).find(p => String(p.product_id) === String(productId));
                    if (product) {
                        const pricingType = productPricingTypes[key] || orderType;
                        const isRemoved = swapData.removed.find(
                            item => String(item.product_id) === String(productId) && item.product_type === pricingType
                        );
                        const existingInAdded = allNewProducts.find(
                            item => String(item.product_id) === String(productId) && item.product_type === pricingType
                        );
                        
                        if (!isRemoved && !existingInAdded) {
                            const currentQty = productQuantities[key] || existingQty;
                            allNewProducts.push({
                                product_id: product.product_id,
                                product_name: product.product_name,
                                product_code: product.product_code,
                                product_type: pricingType,
                                qty: currentQty,
                                new_product_unit_price: getProductPrice(product, pricingType)
                            });
                        }
                    }
                }
            });

        setIsSubmittingSwap(true);
        try {
            const response = await changeGetProduct(
                promotion.id,
                swapData.removed,
                allNewProducts,
                selectedPlatform,
                selectedCustomer.id
            );

            if (response.success) {
                clearPromotionSwapData(promotion.id);
                setSwapData({ removed: [], added: [] });
                setShowConfirmDialog(false);
                setSnackbar({
                    open: true,
                    message: 'Promotional products swapped successfully',
                    severity: 'success'
                });
                queryClient.invalidateQueries({
                    queryKey: ['cartList', selectedCustomer.id, selectedPlatform],
                    exact: true,
                });
                queryClient.invalidateQueries({ queryKey: ['cartPromotions'], exact: false });
                await queryClient.refetchQueries({
                    queryKey: ['cartList', selectedCustomer.id, selectedPlatform],
                    exact: true,
                });

                if (!isEpos) {
                    const eventName = 'quickOrderCartUpdated';
                    window.dispatchEvent(new CustomEvent(eventName, {
                        detail: { customerId: selectedCustomer.id }
                    }));
                }
            } else {
                setShowConfirmDialog(false);
                const errorMessage = response.message || response.data?.message || response.error || 'Failed to swap promotional products';
                if (errorMessage.toLowerCase().includes('new_product_id is required')) {
                    setSwapErrorMessage('Please add promotional Products');
                } else {
                    setSwapErrorMessage(errorMessage);
                }
                setShowSwapErrorDialog(true);
            }
        } catch (error) {
            setShowConfirmDialog(false);
            const errorMessage = error?.message || error?.response?.data?.message || 'Failed to swap promotional products. Please try again.';
            if (errorMessage.toLowerCase().includes('new_product_id is required')) {
                setSwapErrorMessage('Please add promotional Products');
            } else {
                setSwapErrorMessage(errorMessage);
            }
            setShowSwapErrorDialog(true);
        } finally {
            setIsSubmittingSwap(false);
        }
    };

    const handleCloseDrawer = () => {
        const missing = getMissingCompulsoryProductCodes(
            promotionDetails,
            productQuantities,
            existingCartQuantities
        );
        if (missing.length > 0) {
            if (hasAcknowledgedCompulsoryDialog) {
                onClose();
                return;
            }
            setCompulsoryDialogCodes(missing);
            setShowCompulsoryDialog(true);
            return;
        }
        if (promotion?.id) {
            const savedSwapData = getPromotionSwapData(promotion.id);
            if (savedSwapData.removed.length > 0 || savedSwapData.added.length > 0) {
                setShowDiscardDialog(true);
                return;
            }
        }
        onClose();
    };

    const handleCloseCompulsoryDialog = () => {
        setShowCompulsoryDialog(false);
        setHasAcknowledgedCompulsoryDialog(true);
    };

    const handleDiscardChanges = () => {
        if (promotion?.id) {
            clearPromotionSwapData(promotion.id);
            setSwapData({ removed: [], added: [] });
        }
        setShowDiscardDialog(false);
        onClose();
    };

    const sortCompulsoryFirst = (a, b) => {
        const aComp = isCompulsoryOfferProduct(a) ? 1 : 0;
        const bComp = isCompulsoryOfferProduct(b) ? 1 : 0;
        return bComp - aComp;
    };

    const filteredBuyProducts = (() => {
        const searchFiltered = buyProductsWithLimits.filter(
            (product) =>
                (product?.product_name?.toLowerCase()?.includes(searchTerm.toLowerCase()) || false) ||
                (product?.product_code?.includes(searchTerm) || false)
        );
        const groupFiltered =
            buyProductGroupIds.length > 1 && selectedBuyGroupId != null
                ? searchFiltered.filter(
                      (p) => String(resolveProductGroupId(p)) === String(selectedBuyGroupId)
                  )
                : searchFiltered;
        return groupFiltered.sort(sortCompulsoryFirst);
    })();

    const filteredOfferProducts = (() => {
        const searchFiltered = (promotionDetails.get_products || []).filter(
            (product) =>
                (product?.product_name?.toLowerCase()?.includes(searchTerm.toLowerCase()) || false) ||
                (product?.product_code?.includes(searchTerm) || false)
        );
        const groupFiltered =
            getProductGroupIds.length > 1 && selectedGetGroupId != null
                ? searchFiltered.filter(
                      (p) => String(resolveProductGroupId(p)) === String(selectedGetGroupId)
                  )
                : searchFiltered;
        return groupFiltered.sort(sortCompulsoryFirst);
    })();

    return {
        activeTab,
        searchTerm,
        productQuantities,
        productPricingTypes,
        existingCartQuantities,
        snackbar,
        limitDialog,
        closeLimitDialog,
        swapData,
        showConfirmDialog,
        isSubmittingSwap,
        showDiscardDialog,
        promotionDetails,
        isLoading,
        error,
        hasGetProducts,
        selectedBuyProductsCount,
        selectedGetProductsCount,
        hasOtherPromotionsInCart: shouldLockOfferProducts,
        cartLineItemsForLimits,
        filteredBuyProducts,
        filteredOfferProducts,
        buyProductGroupIds,
        selectedBuyGroupId,
        setSelectedBuyGroupId,
        getProductGroupIds,
        selectedGetGroupId,
        setSelectedGetGroupId,
        selectedCustomer,
        setSearchTerm,
        handleTabChange,
        handleQuantityChange,
        notifyQuantityLimitExceeded,
        handlePricingTypeChange,
        handleAddToCart,
        handleSubmitSwap,
        handleCloseDrawer,
        handleDiscardChanges,
        setShowConfirmDialog,
        setShowDiscardDialog,
        showInvalidProductsDialog,
        setShowInvalidProductsDialog,
        showSwapErrorDialog,
        setShowSwapErrorDialog,
        swapErrorMessage,
        setSnackbar,
        showCompulsoryDialog,
        setShowCompulsoryDialog,
        compulsoryDialogCodes,
        handleCloseCompulsoryDialog
    };
};
